/*
 * Copyright 2024 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import KeyvMemcache from '@keyv/memcache';
import { Instance } from './types';
import { attemptKeyvConnection } from './helpers';

const createStore = (connection: string) => new KeyvMemcache(connection);

export async function connectToExternalMemcache(
  connection: string,
): Promise<Instance> {
  const keyv = await attemptKeyvConnection(
    createStore,
    connection,
    'memcached',
  );
  return {
    store: 'memcache',
    connection,
    keyv,
    stop: async () => await keyv.disconnect(),
  };
}

export async function startMemcachedContainer(
  image: string,
): Promise<Instance> {
  // Lazy-load to avoid side-effect of importing testcontainers
  const { GenericContainer } =
    require('testcontainers') as typeof import('testcontainers');

  const container = await new GenericContainer(image)
    .withExposedPorts(11211)
    .start();

  const host = container.getHost();
  const port = container.getMappedPort(11211);
  const connection = `${host}:${port}`;

  const keyv = await attemptKeyvConnection(
    createStore,
    connection,
    'memcached',
  );

  return {
    store: 'memcache',
    connection,
    keyv,
    stop: async () => {
      await keyv.disconnect();
      await container.stop({ timeout: 10_000 });
    },
  };
}
